import { Card, CardContent, Container, Grid, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { Suspense, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetPortfolioAnalysisSubscriptionsQuery } from '../../../../api/energy-analysis/portfolioAnalysisApiSlice';
import { useGetAccountsQuery } from '../../../../api/recalc/endpoints/accountsApiSlice';
import { getSelectedAccount } from '../../../../redux/selectedAccountSlice';
import PortfolioAnalysisDrawerContainer from './PortfolioAnalysisDrawerContainer';
import PortfolioAnalysisManagementDatagrid from '@/features/service-management/components/portfolio-analysis/PortfolioAnalysisManagementDatagrid';

const PortfolioAnalysisManagementPage = () => {
  const [selectedRow, setSelectedRow] = useState({});
  const selectedAccount = useSelector(getSelectedAccount);

  const { data: accounts, isFetching } = useGetAccountsQuery();
  const { data: subscriptionData } =
    useGetPortfolioAnalysisSubscriptionsQuery();

  const rows = useMemo(() => {
    let result = subscriptionData ?? [];
    if (accounts === undefined || accounts?.length === 0) return [];

    result = result.map((g) => {
      let a = accounts.find((a) => g.account_id === a.AccountId);
      if (a === undefined) return { ...g, id: g.account_id };

      return {
        AccountId: a.AccountId,
        Name: a.Name,
        hasService:
          a.Services.length !== 0 &&
          a.Services.some((s) => s.Name === 'PortfolioAnalysis'),
        ...g,
        id: a.AccountId,
      };
    });
    return result;
  }, [accounts, subscriptionData]);

  const stats = useMemo(() => {
    if (subscriptionData === undefined || subscriptionData?.length === 0)
      return [];

    const totalHeat = subscriptionData.reduce((acc, g) => {
      return g.heat ? acc + 1 : acc;
    }, 0);

    const totalPower = subscriptionData.reduce((acc, g) => {
      return g.power ? acc + 1 : acc;
    }, 0);

    const totalWater = subscriptionData.reduce((acc, g) => {
      return g.water ? acc + 1 : acc;
    }, 0);

    const totalAccounts = subscriptionData.length;

    return [
      { name: '# Power', value: totalPower },
      { name: '# Water', value: totalWater },
      { name: '# Heat', value: totalHeat },
      { name: '# Accounts', value: totalAccounts },
    ];
  }, [subscriptionData]);

  return (
    <Container maxWidth="xxl">
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              verticalAlign: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        }
      >
        <Box sx={{ display: 'flex', height: '70vh', width: '100%' }}>
          <Grid container spacing={2}>
            {stats.map((s, index) => (
              <Grid key={index} item lg={3}>
                <ServiceCard {...s} />
              </Grid>
            ))}
            <Grid item lg={12} maxHeight={'80vh'}>
              <PortfolioAnalysisManagementDatagrid
                initialRows={rows}
                isLoading={isFetching}
                setSelectedRow={setSelectedRow}
                account={selectedAccount}
              />
            </Grid>
          </Grid>
        </Box>
      </Suspense>
      <PortfolioAnalysisDrawerContainer
        isOpen={selectedRow?.Name}
        onClose={() => setSelectedRow({})}
        account={selectedRow}
      />
    </Container>
  );
};

export default PortfolioAnalysisManagementPage;

const ServiceCard = (props) => {
  const { name, value } = props;

  return (
    <Card variant="outlined">
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            mb: 2,
          }}
        >
          <Typography variant="body1" color="textSecondary" fontWeight="bold">
            {name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
          >
            <Typography variant="h4" color="textPrimary" fontWeight={'bold'}>
              {value}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
